import { RESOLUTION_LEVELS, type ResolutionLevel, type SubjectStatus } from '@ni/shared';
import { RESOLUTION_LEVEL_LABELS, subjectStatusLabel } from '../../lib/copy';

/**
 * **The two controls that settle an entry** — the level it becomes and, where it
 * becomes a tracked person, the status it gets.
 *
 * They were written inline on the row card, so a name the file printed twice —
 * drawn as one card with a line per copy — had neither: the copies showed their
 * level as a *word*, and the only way to change one was to answer the group's
 * question, re-run the dry run, and wait for the copies to become ordinary rows
 * again. They are the same controls in both places because they are the same
 * gesture, and one copy of them is the only way that stays true.
 */

export function RowLevelSelect({
  who,
  level,
  describedBy,
  disabled,
  onLevel,
}: {
  /** What the control is about, said the way the office says it. */
  who: string;
  level: ResolutionLevel;
  /** The hint under the control, where the caller draws one. */
  describedBy?: string;
  disabled: boolean;
  onLevel: (level: ResolutionLevel) => void;
}) {
  return (
    <select
      className="control"
      aria-label={`What ${who} becomes`}
      aria-describedby={describedBy}
      value={level}
      disabled={disabled}
      onChange={(event) => onLevel(event.target.value as ResolutionLevel)}
    >
      {RESOLUTION_LEVELS.map((candidate) => (
        <option key={candidate} value={candidate}>
          {RESOLUTION_LEVEL_LABELS[candidate]}
        </option>
      ))}
    </select>
  );
}

/**
 * **Only a subject has a status** (§1.3), so the caller draws this where the
 * level says subject — the rule, rather than the row's stored level.
 *
 * The empty option is the one thing this select must never *post*. An entry with
 * no status carries `''`, and choosing it again sent `{status: ''}` — a body the
 * contract refuses with a message about a status word, on a gesture the office
 * did not think it made. A vertical with no statuses of its own draws nothing.
 */
export function RowStatusSelect({
  who,
  status,
  vertical,
  statuses,
  disabled,
  onStatus,
}: {
  who: string;
  status: SubjectStatus | null;
  vertical: string;
  statuses: readonly SubjectStatus[];
  disabled: boolean;
  onStatus: (status: SubjectStatus) => void;
}) {
  if (statuses.length === 0) return null;

  return (
    <select
      className="control"
      aria-label={`The status ${who} gets`}
      value={status ?? ''}
      disabled={disabled}
      onChange={(event) => {
        const chosen = event.target.value;
        if (chosen === '') return;
        onStatus(chosen as SubjectStatus);
      }}
    >
      {status === null ? <option value="">Choose a status</option> : null}
      {statuses.map((candidate) => (
        <option key={candidate} value={candidate}>
          {subjectStatusLabel(vertical, candidate) ?? candidate}
        </option>
      ))}
    </select>
  );
}
